import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import GenreSelect from '@intelligence/components/GenreSelect';
import { renderGenreBotton } from '@intelligence/components/GenreSelectMiddleware/Supports/utils';
import { getMainGenresAndSubGenres } from '@intelligence/components/GenreSelectMiddleware/utils';
import { getGenreOptionsMap, getGenreSelectValue } from './utils';
import { FILTER_TYPE } from '../filterTypes';

import type { IFilterRenderProps, IFilterTypeEntry } from '@intelligence/v3/filterCore/registry';
import type { IFilterControlModule } from '../controlModule';
import type { IGenreFilterValue } from './types';

import ICON_APPANNIE from '@intelligence/assets/svg/dataSource/app-annie.svg';
import ICON_DATABRAIN from '@intelligence/assets/svg/dataSource/databarin-bg.svg';
import ICON_SENSORTOWER from '@intelligence/assets/svg/dataSource/sensor-tower.svg';
import ICON_APPMAGIC from '@intelligence/assets/imgs/dataSource/app-magic.png';
import ICON_WX from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import ICON_ROBLOX from '@intelligence/assets/svg/icon-roblox.svg';

const GENRE_SOURCE_ICON_MAP: Record<string, string> = {
  iegg: ICON_DATABRAIN,
  annie: ICON_APPANNIE,
  appannie: ICON_APPANNIE,
  sensortower: ICON_SENSORTOWER,
  appmagic: ICON_APPMAGIC,
  wechat_minigame: ICON_WX,
  roblox: ICON_ROBLOX,
};

const GenreSelectRenderer: React.FC<IFilterRenderProps<IGenreFilterValue>> = (props) => {
  const { style, className, field, value, onChange, genreOptionsLoading, genresOptionsDataLoading, ...rest } = props;

  const genreOptionsMap = getGenreOptionsMap({
    sources: rest.sources,
    genresOptionsData: rest.genresOptionsData,
    genreOptions: rest.genreOptions,
    options: rest.options,
    genreSources: rest.genreSources,
  });
  const genreSources = rest.genreSources || rest.sources;

  return (
    <div style={style} className={classnames('dbi-genre-source-segment-filter select-item', className)} key={field}>
      <GenreSelect
        {...rest}
        loading={genresOptionsDataLoading ?? genreOptionsLoading}
        sources={genreSources}
        options={genreOptionsMap}
        value={getGenreSelectValue(value)}
        onConfirm={(val) => {
          onChange(val);
        }}
      />
    </div>
  );
};

const GenrePreview: React.FC<{
  valueItem: IGenreFilterValue;
  genreOptions: IRecord<any[]>;
}> = (props) => {
  const { valueItem, genreOptions } = props;
  const { locale, formatMessage } = useIntl();
  const { source, genres } = valueItem || {};
  const genresLength = genres?.length || 0;
  const currentSourceOptions = genreOptions?.[source] || [];
  const dataSourceIcon = GENRE_SOURCE_ICON_MAP[source] || ICON_APPANNIE;

  const renderGenreButton = () => {
    if (!genresLength) {
      return formatMessage({ id: 'dbi_all' });
    }

    if (genresLength > 1) {
      return genresLength;
    }

    return renderSingleGenreText();
  };

  const renderSingleGenreText = (): string => {
    const { mainGenres, subGenres } = getMainGenresAndSubGenres(valueItem, currentSourceOptions);
    return renderGenreBotton(locale, mainGenres, subGenres);
  };

  return (
    <>
      <span className="model-form-text">{formatMessage({ id: 'dbi_top_genre' })}</span>
      <img style={{ marginRight: 4, width: 16, verticalAlign: 'middle' }} src={dataSourceIcon} alt="" />
      <span className="model-form-context">{renderGenreButton()}</span>
    </>
  );
};

// eslint-disable-next-line react/display-name
function renderGenrePreview(
  item: Parameters<NonNullable<IFilterTypeEntry['renderPreview']>>[0],
  value: any,
): React.ReactNode {
  const genreOptions = item.controlProps?.genresOptionsData || item.controlProps?.options || {};

  return <GenrePreview valueItem={value} genreOptions={genreOptions} />;
}

const getRenderPreview = (): NonNullable<IFilterTypeEntry['renderPreview']> => {
  return renderGenrePreview;
};

export const genreSelectControl: IFilterControlModule = {
  type: FILTER_TYPE.GENRE_SELECT,
  entry: {
    render: (props) => {
      return <GenreSelectRenderer {...props} />;
    },
  },
  getRenderPreview,
};
